import { CheckCircle2, Circle, Loader2, MinusCircle, XCircle } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { GCP_CONNECT_STEP_LABELS } from "@mycompbox/shared";
import { Button } from "@/components/ui/button";
import { useGcpSetupJob } from "@/hooks/queries/use-cloud";
import type { GcpSetupStepState } from "@/lib/api/cloud";
import { gcpWizardPath } from "./wizard-state";

const EXTRA_LABELS: Record<string, string> = {
  sql: "Create the database",
  secret: "Store the database password",
  budget: "Create the monthly budget",
  deploy: "First deploy",
};
const CHIP_LABELS: Record<GcpSetupStepState, string> = {
  pending: "Waiting",
  running: "Working",
  done: "Done",
  skipped: "Skipped",
  failed: "Failed",
};
const labelFor = (step: string): string =>
  (GCP_CONNECT_STEP_LABELS as Record<string, string>)[step] ?? EXTRA_LABELS[step] ?? step;

function Icon({ state }: { state: GcpSetupStepState }) {
  if (state === "done") return <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden />;
  if (state === "running") return <Loader2 className="h-4 w-4 animate-spin" aria-hidden />;
  if (state === "failed") return <XCircle className="h-4 w-4 text-destructive" aria-hidden />;
  if (state === "skipped") return <MinusCircle className="h-4 w-4 text-muted-foreground" aria-hidden />;
  return <Circle className="h-4 w-4 text-muted-foreground" aria-hidden />;
}

/** Step 5: the background setup, item by item. Progress survives reloads because the job lives on the server. */
export function BuildStep() {
  const { data: job, isLoading, isError } = useGcpSetupJob();
  const navigate = useNavigate();

  if (isLoading) return <p className="text-sm text-muted-foreground">Checking the setup…</p>;
  if (isError) {
    return (
      <p role="alert" className="text-sm text-destructive">
        Couldn't read the setup's progress. Reload to try again.
      </p>
    );
  }
  if (!job) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">No setup is running.</p>
        <Button asChild>
          <Link to={gcpWizardPath("start")}>Start over</Link>
        </Button>
      </div>
    );
  }

  const stopped = job.status === "failed" || job.status === "interrupted";
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">
        {job.status === "succeeded" ? "Your Google Cloud project is built" : stopped ? "The setup stopped" : "Building your Google Cloud project…"}
      </h2>
      <ol className="space-y-2 text-sm">
        {Object.entries(job.steps).map(([step, state]) => (
          <li key={step}>
            <div className="flex items-center gap-2">
              <Icon state={state} />
              <span className={state === "pending" ? "text-muted-foreground" : undefined}>{labelFor(step)}</span>
              <span
                data-testid={`chip-${step}`}
                data-state={state}
                className="ml-auto rounded-full border px-2 py-0.5 text-[11px] text-muted-foreground"
              >
                {CHIP_LABELS[state]}
              </span>
            </div>
            {state === "skipped" && job.skipReasons[step] && (
              <p className="ml-6 text-xs text-muted-foreground">{job.skipReasons[step]}</p>
            )}
          </li>
        ))}
      </ol>
      {stopped && (
        <p role="alert" className="text-sm text-destructive">
          {job.status === "interrupted"
            ? "The setup was interrupted. Sign in with Google again to carry on; finished items are kept."
            : (job.error ?? "The setup failed.")}
        </p>
      )}
      <div className="flex gap-2">
        {job.status === "succeeded" && <Button onClick={() => navigate(gcpWizardPath("live"))}>Continue</Button>}
        {stopped && (
          <Button asChild>
            <Link to={gcpWizardPath("sign-in")}>Sign in and resume</Link>
          </Button>
        )}
      </div>
    </div>
  );
}
